<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import IspBarChart from './components/IspBarChart.vue';
import IspDataTable from './components/IspDataTable.vue';
import IspChartjsLine from './components/IspChartjsLine.vue';

import { useIspReport } from './composables/useIspReport';
import { exportCsv } from './composables/useIspExport';
import { formatTime, formatTrend } from './composables/ispFormatters';
// GK-HUB: Import role helper for team filter visibility
import { getUserRole } from 'dashboard/helper/permissionsHelper';

const store = useStore();
const { t } = useI18n();

// GK-HUB: Removed showDeptFilter/departments/selectedDeptId — using team filter instead
const {
  data: motivos,
  retry,
  customDateRange,
  selectedDateRange,
  onDateRangeChange,
  onFilterChange,
  filteredAgents,
} = useIspReport({
  getter: 'ispReports/getMotivos',
  action: 'ispReports/fetchMotivos',
  filters: { inboxId: '', agentId: '', status: '', teamId: '' },
  initActions: ['inboxes/get', 'agents/get'],
});

// GK-HUB: Teams list + showTeamFilter (admin/manager only, supervisor auto-scoped on backend)
const teams = computed(() => store.getters['teams/getTeams']);
store.dispatch('teams/get');
const currentUser = computed(() => store.getters.getCurrentUser);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const showTeamFilter = computed(() =>
  ['administrator', 'manager'].includes(
    getUserRole(currentUser.value, currentAccountId.value)
  )
);
// GK-HUB: end team filter

const inboxes = computed(() => store.getters['inboxes/getInboxes']);

// ── Summary (5 mini KPIs) ──
const summary = computed(() => motivos.value.data?.summary || {});

// ── Volume bar items (left card) ──
// Cyclic color palette from tokens.css — supports unlimited motivos
const MOTIVO_COLORS = ['blue', 'cyan', 'success', 'warning', 'error', 'dark'];
const getMotivosColor = idx => MOTIVO_COLORS[idx % MOTIVO_COLORS.length];

const barItems = computed(() =>
  (motivos.value.data?.motivos || []).map((m, i) => ({
    label: m.name,
    value: m.count,
    displayValue: String(m.count),
    percentage: m.percentage,
    color: getMotivosColor(i),
  }))
);

// ── TMA bar items (right card) ──
const getTmaBarColor = index => {
  if (index === 0) return 'error';
  if (index < 3) return 'warning';
  return 'blue';
};
const tmaBarItems = computed(() =>
  (motivos.value.data?.tma_por_motivo || []).map((m, i) => ({
    label: m.name,
    value: formatTime(m.tma_seconds),
    displayValue: formatTime(m.tma_seconds),
    percentage: m.percentage,
    color: getTmaBarColor(i), // tokens: error, warning, brand-blue
  }))
);

// ── Evolution chart ──
const chartSeries = computed(() => {
  const evolucao = motivos.value.data?.evolucao || [];
  if (!evolucao.length) return [];

  const allMotivos = new Set();
  evolucao.forEach(e =>
    Object.keys(e.data || {}).forEach(m => allMotivos.add(m))
  );

  // Colors from tokens.css: brand-cyan, brand-blue, color-warning, color-error, brand-blue-light
  const colors = ['#5DADE2', '#2E86C1', '#F59E0B', '#EF4444', '#4BA3D9'];
  return [...allMotivos].slice(0, 3).map((motivo, idx) => ({
    name: motivo,
    color: colors[idx] || colors[0],
    data: evolucao.map(e => ({
      value: e.data?.[motivo] || 0,
      label: new Date(e.timestamp * 1000).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
      }),
    })),
  }));
});

// ── Data Table ──
const tableColumns = [
  { key: 'motivo', label: t('ISP_REPORTS.MOTIVOS.REASON'), width: '22%' },
  {
    key: 'total',
    label: t('ISP_REPORTS.MOTIVOS.TOTAL'),
    align: 'right',
    type: 'mono',
    width: '9%',
  },
  {
    key: 'percentage',
    label: t('ISP_REPORTS.MOTIVOS.PERCENTAGE'),
    align: 'right',
    width: '9%',
  },
  {
    key: 'tma',
    label: t('ISP_REPORTS.MOTIVOS.TMA'),
    align: 'right',
    type: 'mono',
    width: '13%',
  },
  {
    key: 'fcr',
    label: t('ISP_REPORTS.MOTIVOS.FCR'),
    align: 'right',
    type: 'badge',
    width: '12%',
  },
  {
    key: 'trend',
    label: t('ISP_REPORTS.MOTIVOS.TREND'),
    align: 'right',
    type: 'trend',
    width: '11%',
  },
  // GK-HUB: IA resolution/transfer rate columns
  {
    key: 'ia_resolution_rate',
    label: t('ISP_REPORTS.MOTIVOS.IA_RESOLUTION_RATE'),
    align: 'right',
    type: 'badge',
    width: '12%',
  },
  {
    key: 'ia_transfer_rate',
    label: t('ISP_REPORTS.MOTIVOS.IA_TRANSFER_RATE'),
    align: 'right',
    type: 'badge',
    width: '12%',
  },
  // GK-HUB: end IA rate columns
];

const tableRows = computed(() =>
  (motivos.value.data?.tabela || []).map(row => ({
    motivo: row.motivo,
    total: row.total,
    percentage: `${row.percentage}%`,
    tma: formatTime(row.tma_seconds),
    fcr: `${row.fcr_rate || 0}%`,
    trend: formatTrend(row.trend),
    // GK-HUB: IA resolution/transfer rates
    ia_resolution_rate: `${row.ia_resolution_rate ?? 0}%`,
    ia_transfer_rate: `${row.ia_transfer_rate ?? 0}%`,
  }))
);

// ── Export CSV ──
const onExportCsv = () => {
  const rows = motivos.value.data?.tabela || [];
  exportCsv(
    rows,
    'Motivo,Total,%,TMA (s),FCR (%),Tendência (%),Resolução IA (%),Transferência IA (%)\n',
    r =>
      `"${r.motivo}",${r.total},${r.percentage},${r.tma_seconds},${r.fcr_rate || 0},${r.trend || 0},${r.ia_resolution_rate ?? 0},${r.ia_transfer_rate ?? 0}`,
    'motivos-contato.csv'
  );
};

// ── Status options for filter ──
const statusOptions = [
  { value: '', label: t('ISP_REPORTS.MOTIVOS.FILTER_ALL_STATUS') },
  { value: 'resolved', label: 'Resolvido' },
  { value: 'open', label: 'Em aberto' },
  { value: 'pending', label: 'Pendente' },
];
</script>

<template>
  <div class="p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('ISP_REPORTS.MOTIVOS.HEADER') }}
        </h1>
        <p class="text-[13px] text-n-slate-10 mt-0.5">
          {{ t('ISP_REPORTS.MOTIVOS.SUBTITLE') }}
        </p>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex items-center gap-3 mb-6 flex-wrap">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <!-- GK-HUB: Team filter (admin/manager only) -->
      <div v-if="showTeamFilter" class="isp-select-wrap">
        <select class="isp-select" @change="e => onFilterChange('teamId', e)">
          <option value="">
            {{ t('ISP_REPORTS.PAINEL.TODOS_TIMES') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <!-- GK-HUB: end team filter -->
      <div class="isp-select-wrap">
        <select class="isp-select" @change="e => onFilterChange('inboxId', e)">
          <option value="">
            {{ t('ISP_REPORTS.FILTERS.ALL_INBOXES') }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <div class="isp-select-wrap">
        <select class="isp-select" @change="e => onFilterChange('agentId', e)">
          <option value="">
            {{ t('ISP_REPORTS.FILTERS.ALL_AGENTS') }}
          </option>
          <!-- GK-HUB: Use filteredAgents (role=agent only) -->
          <option
            v-for="agent in filteredAgents"
            :key="agent.id"
            :value="agent.id"
          >
            {{ agent.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <div class="isp-select-wrap">
        <select class="isp-select" @change="e => onFilterChange('status', e)">
          <option
            v-for="opt in statusOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <button
        class="btn-export border-n-slate-7 text-n-slate-10 hover:border-n-slate-9 hover:text-n-slate-12"
        @click="onExportCsv"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        {{ t('ISP_REPORTS.FILTERS.EXPORT_CSV') }}
      </button>
    </div>

    <!-- Loading (initial only) -->
    <woot-loading-state
      v-if="motivos.isFetching && !motivos.data"
      :message="t('ISP_REPORTS.LOADING')"
      class="h-64"
    />

    <!-- Error (initial only) -->
    <div
      v-else-if="motivos.error && !motivos.data"
      class="flex flex-col items-center justify-center h-64 gap-3"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.ERROR_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.ERROR_MESSAGE') }}
      </p>
      <button
        class="bg-n-brand text-white rounded-md text-sm font-medium px-4 py-2"
        @click="retry"
      >
        {{ t('ISP_REPORTS.RETRY') }}
      </button>
    </div>

    <template v-if="motivos.data">
      <!-- ── 5 Mini KPIs ── -->
      <div class="mini-kpi-row">
        <div class="mini-kpi">
          <div class="mini-kpi__label">
            {{ t('ISP_REPORTS.MOTIVOS.SUMMARY_TOTAL') }}
          </div>
          <div class="mini-kpi__value">
            {{ summary.total || 0 }}
          </div>
        </div>
        <div class="mini-kpi">
          <div class="mini-kpi__label">
            {{ t('ISP_REPORTS.MOTIVOS.SUMMARY_TOP') }}
          </div>
          <div class="mini-kpi__value mini-kpi__value--highlight">
            {{ summary.top_motivo || '-' }}
          </div>
        </div>
        <div class="mini-kpi">
          <div class="mini-kpi__label">
            {{ t('ISP_REPORTS.MOTIVOS.SUMMARY_TOP_PCT') }}
          </div>
          <div class="mini-kpi__value mini-kpi__value--highlight">
            {{ summary.top_pct || 0 }}%
          </div>
        </div>
        <div class="mini-kpi">
          <div class="mini-kpi__label">
            {{ t('ISP_REPORTS.MOTIVOS.SUMMARY_TMA_HIGH') }}
          </div>
          <div class="mini-kpi__value mini-kpi__value--warning">
            {{ summary.tma_highest_motivo || '-' }}
          </div>
        </div>
      </div>

      <!-- ── 2 Cards Side by Side (60/40): Volume + TMA ── -->
      <div
        class="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4 mb-6 items-stretch"
      >
        <IspBarChart
          :title="t('ISP_REPORTS.MOTIVOS.DETAIL_BY_REASON')"
          :subtitle="t('ISP_REPORTS.MOTIVOS.DETAIL_BY_REASON_SUB')"
          :items="barItems"
        />
        <IspBarChart
          :title="t('ISP_REPORTS.MOTIVOS.TMA_BY_REASON')"
          :subtitle="t('ISP_REPORTS.MOTIVOS.TMA_BY_REASON_SUB')"
          :items="tmaBarItems"
          label-width="110px"
        />
      </div>

      <!-- ── Line Chart: Evolution (Chart.js) ── -->
      <IspChartjsLine
        v-if="chartSeries.length"
        :title="t('ISP_REPORTS.MOTIVOS.EVOLUTION')"
        :subtitle="t('ISP_REPORTS.MOTIVOS.EVOLUTION_SUB')"
        :series="chartSeries"
        :height="280"
      />

      <!-- ── Data Table ── -->
      <IspDataTable
        :title="t('ISP_REPORTS.MOTIVOS.DETAILED_TABLE')"
        :columns="tableColumns"
        :rows="tableRows"
      />
    </template>

    <!-- Empty State -->
    <div
      v-else-if="!motivos.isFetching && !motivos.error"
      class="flex flex-col items-center justify-center h-64 gap-2"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.EMPTY_MOTIVOS_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.EMPTY_MOTIVOS_MESSAGE') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* ── Filter select wrapper (same pattern as IspConversas) ──────────── */
.isp-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.isp-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  pointer-events: none;
  color: #9ca3af;
  flex-shrink: 0;
}

/* ── Filter select: base (light mode default) ─────────────────────── */
.isp-select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 100%;
  height: auto;
  margin: 0;
  font-size: 12px;
  font-family: inherit;
  line-height: 1.5;
  padding: 6px 30px 6px 10px;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
  background: none;
  /* Light mode colors */
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
}
.isp-select:hover {
  border-color: #cbd0d6;
}
.isp-select:focus {
  border-color: #2e86c1;
}

/* ── Mini KPI Row ─────────────────────────────────────────────────── */
.mini-kpi-row {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.mini-kpi {
  flex: 1 1 0;
  min-width: 140px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid var(--color-border-light, #e5e7eb);
  background: var(--color-bg-light, #fafbfc);
}
.mini-kpi__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
  margin-bottom: 6px;
}
.mini-kpi__value {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}
.mini-kpi__value--highlight {
  font-size: 15px;
  margin-top: 4px;
  color: #ef4444;
}
.mini-kpi__value--warning {
  font-size: 15px;
  margin-top: 4px;
  color: #f59e0b;
}

/* ── Export button (same pattern as IspFcr) ──────────────────────── */
.btn-export {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border-width: 1.5px;
  border-style: solid;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
}

/* ── DatePicker calendar fix ──────────────────────────────────────── */
:deep(.date-picker) {
  position: relative;
  z-index: 20;
}
:deep(.date-picker .datepicker--container),
:deep(.date-picker [class*='absolute']) {
  z-index: 50 !important;
}
:deep(.daterange-picker--container),
:deep(.datepicker--container) {
  z-index: 50 !important;
}
</style>

<!-- Dark mode overrides (unscoped for reliable body.dark targeting) -->
<style>
/* ── Filter select: dark mode (IspMotivos scope) ─────────────────── */
body.dark .isp-select {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.72);
  border-color: rgba(255, 255, 255, 0.08);
}
body.dark .isp-select:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background-color: rgba(255, 255, 255, 0.03);
}
body.dark .isp-select-arrow {
  color: #64748b;
}

/* ── Mini KPI: dark mode ─────────────────────────────────────────── */
body.dark .mini-kpi {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.06);
}
body.dark .mini-kpi__label {
  color: #64748b;
}
body.dark .mini-kpi__value {
  color: #e2e8f0;
}
body.dark .mini-kpi__value--highlight {
  color: #f87171;
}
body.dark .mini-kpi__value--warning {
  color: #fbbf24;
}
</style>
